import * as amplitude from "@amplitude/analytics-browser";
import { Box, Flex, Spinner, Text } from "@chakra-ui/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useDeleteConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useOrganization } from "src/hooks/useOrganization";
import { NoScheduledExports } from "./NoScheduledExports";
import { ScheduledExportItem } from "./ScheduledExportItem";
import { useExportManagementAPI } from "./useExportManagementAPI";

export const ScheduledExportsList = () => {
  const organization = useOrganization();
  const toast = useAvelaToast();
  const queryClient = useQueryClient();
  const api = useExportManagementAPI();
  const [deleting, setDeleting] = React.useState<string>();
  const { confirm, confirmationDialog } = useDeleteConfirmationDialog({});
  const location = useLocation();

  // Handle scheduled export update toast from navigation state
  // TODO: Ideally the toast should be in the BulkExportForm component, but the toast disappears after navigation.
  useEffect(() => {
    const { scheduledExportUpdated } = location.state ?? {};
    if (scheduledExportUpdated) {
      toast({
        id: "scheduled-export-updated",
        status: "info",
        title: "Scheduled export updated",
        isClosable: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.scheduledExportUpdated]);

  const {
    data: schedules = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["getScheduledExports", organization.toNullable()?.id],
    queryFn: async () => {
      if (!organization.hasData()) return [];
      return await api.getScheduledExports(organization.data.id);
    },
    enabled: organization.hasData() && api.isReady,
  });

  const onDelete = React.useCallback(
    async (pk: string) => {
      amplitude.track("Clicked delete scheduled export");
      if (!organization.hasData()) return;
      if (!(await confirm())) return;
      setDeleting(pk);
      const toastId = "delete-schedule";
      api
        .deleteSchedule(organization.data.id, pk)
        .then(async () => {
          await queryClient.invalidateQueries({
            queryKey: ["getScheduledExports", organization.data.id],
          });
          amplitude.track("Deleted scheduled export");
          toast({
            id: toastId,
            title: `Schedule deleted`,
            isClosable: true,
            status: "info",
          });
        })
        .catch((error) => {
          toast({
            id: toastId,
            title: `Something went wrong!`,
            description: "Check your network and try again.",
            isClosable: true,
            status: "error",
          });
        })
        .finally(() => setDeleting(undefined));
    },
    [organization, api, queryClient, confirm, toast]
  );

  if (isLoading) {
    return (
      <Box display="grid" minHeight="50vh" placeContent="center">
        <Spinner color="primary.500" size="lg" />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box display="grid" minHeight="50vh" placeContent="center">
        <Text fontSize="xl" textColor="red.500" fontWeight="700">
          Something went wrong...
        </Text>
      </Box>
    );
  }

  return (
    <>
      {schedules.length === 0 && <NoScheduledExports />}

      {schedules.length > 0 && (
        <Flex direction="column" gap="6">
          {schedules.map((schedule) => (
            <ScheduledExportItem
              key={schedule.pk}
              schedule={schedule}
              organization={organization}
              onDelete={onDelete}
              isDeleting={deleting === schedule.pk}
            />
          ))}
          {confirmationDialog}
        </Flex>
      )}
    </>
  );
};
